/**
 * The file page's preview pane. PDFs render as one continuous scroll of
 * pages (`PdfScrollView`); workbooks render in the same sheet view (one parser,
 * one renderer, shared cache via `SpreadsheetViewerProvider`). Anything else
 * says it has no preview and offers the download.
 *
 * Opened from a citation, both viewers start at what it points at — the quote
 * or figure highlighted on its page, the cell or range selected on its sheet —
 * and offer a way back to it.
 */
import { Loader2 } from "lucide-react";
import React, { Suspense, useEffect, useMemo, useState } from "react";
import { useSpreadsheetViewer } from "../../context/SpreadsheetViewerContext";
import { downloadFileBytes } from "../../services/fileApi";
import { FileSubkind, FileType } from "../../types/fileType";
import { friendlyErrorMessage } from "../../utils/apiErrorDetail";
import type { ParsedDopplerUri } from "../../utils/dopplerUri";
import SpreadsheetView from "../SpreadsheetView";
import BackToCitation from "./BackToCitation";
import type { PdfCitation } from "./PdfScrollView";

export type PreviewKind = "pdf" | "sheet" | "none";

export const previewKindFor = (filename: string, mimeType: string): PreviewKind => {
	const name = filename.toLowerCase();
	if (mimeType === "application/pdf" || name.endsWith(".pdf")) return "pdf";
	if (/\.(xlsx|xlsm|xls|csv)$/.test(name)) return "sheet";
	return "none";
};

interface FilePreviewProps {
	fileId: number;
	filename: string;
	mimeType: string;
	onDownload: () => void;
	/** The citation this file was opened for, if any. */
	citation?: ParsedDopplerUri | null;
}

const Centered: React.FC<{ children: React.ReactNode }> = ({ children }) => (
	<div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center text-sm text-text-muted">
		{children}
	</div>
);

// Lazy, so pdf.js (~1 MB plus a worker) loads only when a PDF is opened.
const PdfScrollView = React.lazy(() => import("./PdfScrollView"));

const Spinner: React.FC = () => (
	<Centered>
		<Loader2 className="h-5 w-5 animate-spin" aria-hidden />
	</Centered>
);

const PdfPreview: React.FC<{
	fileId: number;
	onDownload: () => void;
	citation?: PdfCitation;
}> = ({ fileId, onDownload, citation }) => {
	const [data, setData] = useState<ArrayBuffer | null>(null);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		setData(null);
		setError(null);
		downloadFileBytes(fileId).then(
			(bytes) => !cancelled && setData(bytes),
			(err) =>
				!cancelled &&
				setError(friendlyErrorMessage(err, "Couldn't load this file.")),
		);
		return () => {
			cancelled = true;
		};
	}, [fileId]);

	if (error) {
		return (
			<Centered>
				<p>{error}</p>
				<button type="button" onClick={onDownload} className="font-semibold text-[#004C4B]">
					Download instead
				</button>
			</Centered>
		);
	}
	if (!data) return <Spinner />;
	return (
		<Suspense fallback={<Spinner />}>
			<PdfScrollView
				data={data}
				citation={citation}
				onLoadError={(err) =>
					setError(friendlyErrorMessage(err, "Couldn't open this PDF."))
				}
			/>
		</Suspense>
	);
};

const SheetPreview: React.FC<{
	fileId: number;
	citation?: ParsedDopplerUri | null;
}> = ({ fileId, citation }) => {
	const viewer = useSpreadsheetViewer();
	// Bumped by "Back to citation". SpreadsheetView re-asserts the cited sheet
	// and re-centres the cited cell whenever it is handed a new `parsed`.
	const [jump, setJump] = useState(0);
	const parsed = useMemo<ParsedDopplerUri>(
		() => ({
			...(citation ?? {}),
			kind: FileType.EXCEL,
			subkind: FileSubkind.FILE,
			target: String(fileId),
		}),
		// `jump` is the point: a fresh object is what re-asserts the citation.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[fileId, citation, jump],
	);
	if (!viewer) return <Centered>No preview available.</Centered>;
	const cited = citation?.range ?? citation?.cell;
	return (
		// A flex column, so the sheet tabs and footer keep their height and the
		// grid between them scrolls (SpreadsheetView's own default classes).
		<div className="relative flex h-full min-h-0 flex-col bg-white">
			<SpreadsheetView parsed={parsed} loadWorkbook={viewer.loadWorkbook} />
			{cited && (
				<BackToCitation
					where={citation?.sheet ? `${citation.sheet}!${cited}` : cited}
					onClick={() => setJump((n) => n + 1)}
				/>
			)}
		</div>
	);
};

const FilePreview: React.FC<FilePreviewProps> = ({
	fileId,
	filename,
	mimeType,
	onDownload,
	citation,
}) => {
	const pdfCitation = useMemo<PdfCitation | undefined>(
		() =>
			citation?.kind === FileType.PDF
				? {
						page: citation.page ?? 1,
						highlight: citation.highlight,
						numberHighlight: citation.number,
					}
				: undefined,
		[citation],
	);
	switch (previewKindFor(filename, mimeType)) {
		case "pdf":
			return (
				<PdfPreview
					fileId={fileId}
					onDownload={onDownload}
					citation={pdfCitation}
				/>
			);
		case "sheet":
			return (
				<SheetPreview
					fileId={fileId}
					citation={citation?.kind === FileType.EXCEL ? citation : null}
				/>
			);
		default:
			return (
				<Centered>
					<p>No in-app preview for this file type yet.</p>
					<button type="button" onClick={onDownload} className="font-semibold text-[#004C4B]">
						Download {filename}
					</button>
				</Centered>
			);
	}
};

export default FilePreview;
